import { oneLinkUrl, utmParams } from "../shared/links";
import type { PartnerMapContent } from "./types";

/**
 * `utm_source=<the partner's account>&utm_medium=partner&utm_campaign=<slug>_map`, on every link from the page to stippl.io. A partner with
 * no account (Viajero) is measured by its campaign: `utm_source=<pid>`, `utm_campaign=<prefix>`.
 */
const utm = ({ slug, partner, cta }: PartnerMapContent): string =>
	partner.username ? utmParams(partner.username, `${slug}_map`) : utmParams(cta.campaign.pid, cta.campaign.prefix);

/** where stippl.io's front page is in the page's language: the Dutch has its own under /nl */
const frontPage = ({ lang }: PartnerMapContent): string => (lang === "nl" ? "nl" : "");

/**
 * "Save this map": the partner's synced list. Until the list sync has made it (`cta.listId` is empty) it opens the
 * partner's profile instead. The page's loader fills the id in from the list itself (`liveHighlights.ts`), so nobody has
 * to stamp it into the content file; a partner with no account of its own (Viajero) names a list of Stippl's in `cta.listId`.
 * Then this opens `/listpreview/<id>?save=1`, which goes straight to the login / save.
 */
export const saveMapUrl = (content: PartnerMapContent): string => {
	const { listId } = content.cta;
	const { username } = content.partner;
	// no list and no profile: the map has nowhere of its own to save to, so the link is stippl.io's front page
	return `${listId ? `/listpreview/${listId}?save=1&` : username ? `/${username}?` : `/${frontPage(content)}?`}${utm(content)}`;
};

/** "Start planning": the app, through OneLink. `item` is the slug of the place it was tapped on; the base's own sheet has none. */
export const planningUrl = ({ cta }: PartnerMapContent, item?: string): string => oneLinkUrl(cta.campaign.pid, `${cta.campaign.prefix}_${item ?? "map"}`);

/**
 * "Start planning" in a place's sheet. Interim (ClickUp 123yb69a2mr decides what it ends up doing): it is "Save this map", with
 * the place named in the utm so it is measured apart. Until the list exists there is nothing to open, so it stays the OneLink.
 */
export const placePlanningUrl = (content: PartnerMapContent, item: string): string =>
	content.cta.listId ? `${saveMapUrl(content)}&utm_content=poi_start_planning&utm_term=${encodeURIComponent(item)}` : planningUrl(content, item);

/** The "Stippl" links: the map's logo and the small print. */
export const stipplUrl = (content: PartnerMapContent): string => `https://www.stippl.io/${frontPage(content)}?${utm(content)}`;

/** The hostel of a Mad Monkey hostel map (`/p/mad-monkey-<hostel>`); none for another partner's page. */
const hostelOf = ({ slug, cta }: PartnerMapContent): string | null => (cta.campaign.pid === "madmonkey" ? slug.replace(/^mad-monkey-/, "") : null);

/** The map's logo on a computer: stippl.io, and on a Mad Monkey hostel's map the embed utm of its other links to stippl.io. */
export const logoUrl = (content: PartnerMapContent): string => {
	const hostel = hostelOf(content);
	return hostel
		? `https://www.stippl.io/?utm_source=madmonkey&utm_medium=embed&utm_campaign=${encodeURIComponent(hostel)}&utm_content=map_logo`
		: stipplUrl(content);
};

/** The map's logo on a phone: on a Mad Monkey hostel's map the app, through OneLink (it sends a computer to the App Store too, so only a phone gets it); else as on a computer. */
export const logoPhoneUrl = (content: PartnerMapContent): string => {
	const hostel = hostelOf(content);
	return hostel ? `${oneLinkUrl(content.cta.campaign.pid, `${content.cta.campaign.prefix}_${hostel}`)}&af_adset=map_logo` : logoUrl(content);
};
